import React from "react";
import {
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Modal,
  Box,
  TextField,
  Autocomplete,
  Alert,
} from "@mui/material";
import * as Styled from "./styles";
import { useCompanies } from "shared/hooks/useCompanies";
import { ICompany } from "modules/companies/types";
import { IUsersTemp } from "../types";
import { create, all, expireNow } from "modules/users-temp/services";
import { formatDate } from "shared/parsers/date";

const UsersTemp: React.FC = () => {
  const [data, setData] = React.useState<IUsersTemp[]>([]);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>("");
  const [selectedCompanies, setSelectedCompanies] = React.useState<ICompany[]>(
    []
  );
  const { companies } = useCompanies();

  const handleNewUserTemp = async () => {
    setLoading(true);
    const companyIds: string[] = [];
    selectedCompanies.forEach((item) => companyIds.push(item.id.toString()));
    const dataUserTemp: IUsersTemp = {
      email,
      companyIds: JSON.stringify(companyIds),
    };

    const { status } = await create(dataUserTemp);

    if ([200, 201].includes(status)) {
      setLoading(false);
      setSuccess(true);
      handleData();
    }
  };

  const handleCancel = React.useCallback(() => {
    setSelectedCompanies([]);
    setEmail("");
    setLoading(false);
    setSuccess(false);
    setModalOpen(false);
  }, []);

  const handleExpireNow = async (id: number) => {
    await expireNow(id);
    handleData();
  };

  const handleData = React.useCallback(async () => {
    const { status, data } = await all();
    if ([200, 201].includes(status)) {
      setData(data);
    }
  }, []);

  React.useEffect(() => {
    handleData();
  }, [handleData]);

  return (
    <Container maxWidth="lg">
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginBottom: 5, overflowX: "auto" }}
      >
        <Typography variant="h5" component="h4" fontWeight="bold">
          Criador de usuário temporário
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setModalOpen(true)}
          disabled={loading}
        >
          Criar Novo Acesso
        </Button>
      </Grid>
      <Grid container>
        <Paper
          sx={{
            width: "100%",
            borderRadius: 4,
            boxShadow:
              "rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px",
          }}
        >
          <TableContainer>
            <Table aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Expira em</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item, index) => (
                  <TableRow key={`idxs${index}`}>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>
                      {formatDate(new Date(item.expire_at))}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        onClick={() => handleExpireNow(item.id)}
                        disabled={loading}
                      >
                        Expirar Agora
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      <Modal
        open={modalOpen}
        onClose={() => handleCancel()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid #CCC",
            boxShadow: 24,
            p: 4,
            width: "600px",
          }}
        >
          {!success ? (
            <>
              <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                Novo Acesso Temporário
              </Typography>
              <hr />
              <Styled.ContainerVideo>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <br />
                <br />
                <Autocomplete
                  autoHighlight
                  multiple
                  options={companies}
                  getOptionLabel={(label) => label.fantasy_name}
                  value={selectedCompanies}
                  onChange={(_, selected?) => setSelectedCompanies(selected)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Selecione uma ou mais empresas"
                    />
                  )}
                />
                <br />
                <Alert color="warning">
                  O acesso terá a duração de 15 dias após sua criação e será
                  expirado automáticamente.
                </Alert>
              </Styled.ContainerVideo>
              <Styled.ContainerButton>
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => handleNewUserTemp()}
                  disabled={loading}
                >
                  {!loading ? "Criar" : <CircularProgress size={20} />}
                </Button>
                &nbsp;&nbsp;
                {!loading && (
                  <Button type="button" onClick={() => handleCancel()}>
                    Cancelar
                  </Button>
                )}
              </Styled.ContainerButton>
            </>
          ) : (
            <>
              <Styled.ContainerVideo>
                <Alert color="success">
                  O usuário foi criado e será enviado um link de acesso para o
                  e-mail informado.
                </Alert>
              </Styled.ContainerVideo>
              <Styled.ContainerButton>
                <Button type="button" onClick={() => handleCancel()}>
                  Fechar
                </Button>
              </Styled.ContainerButton>
            </>
          )}
        </Box>
      </Modal>
    </Container>
  );
};

export { UsersTemp };
